/**
 * Maps every feature passed to the mixin as computed properties
 * @param {Array} props - An array of features, each containing a name, for the computed properties name
 * and a value to be looked for in the array of active feature tags.
 */
function mapComputedProperties(props = []) {
  if (!props.length) return {};

  return props.reduce((obj, prop) => {
    if (!prop.name || !prop.value) {
      throw new Error('Each feature has to have a "name" and "value" key.');
    }

    const computed = {
      get() {
        return this.activeFeatureTags.includes(prop.value);
      }
    };

    obj[prop.name] = computed;

    return obj;
  }, {});
}

/**
 * A mixin to verify an user's active feature tags
 * @param {Array} features - An array of features, each containing a name, for the computed properties name
 * and a value to be looked for in the array of active feature tags. Every feature passed to the mixin will be checked
 * in the components "created" lifecycle method, if the user has the feature tag, it will be added to the "activeFeatureTags" array.
 */
const verifyFeatureTester = features => ({
  data() {
    return {
      myID: null,
      activeFeatureTags: [],
      isLoadingFeatures: true
    };
  },

  computed: {
    ...mapComputedProperties(features)
  },

  methods: {
    /**
     * Get user's id.
     */
    async loadUserData() {
      try {
        const {
          data: { id }
        } = await axios.get("/api/v2/me/");

        this.myID = id;
      } catch (error) {
        console.error(error);
      }
    },

    /**
     * Returns if the current user has the passed feature test tag.
     * @param {String} user - User's ID.
     * @param {String} feature - Feature test tag to be verified.
     */
    async verifyFeatureTester(user, feature) {
      try {
        const {
          data: { isUserTester }
        } = await axios.get(`/api/v2/feature-tester/${user}/${feature}`);

        if (isUserTester) {
          this.activeFeatureTags.push(feature);
        }
      } catch (error) {
        console.error(error);
      }
    },
  },

  async created() {
    this.isLoadingFeatures = true;
    const query = new URLSearchParams(window.location.search);

    if (query.get('public')) {
      return;
    };

    await this.loadUserData();

    features.forEach(async feature => {
      await this.verifyFeatureTester(this.myID, feature.value);
    });

    this.isLoadingFeatures = false;
  },
});

export default verifyFeatureTester;
